import { Edit, CalendarToday, DynamicFeed } from '@mui/icons-material';
import { Box, Button, Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import type { Asset } from 'types/asset.types';
import { AssetHeader } from './Header';
import { isSensorEnabled } from 'views/TirePressure/util';
import { useRoleBasedAccess } from 'hooks/useRoleBasedAccess';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const AssetDetail = ({
  enableDarkTheme,
  selectedAsset,
  latestTagData,
  hierarchyInfo,
  setIsSelectModalOpen,
  setIsFitmentViewOpen,
  setIsTirePositionOpen,
  setIsAxleWiseModalOpen,
  tireData,
  batteryData,
}: {
  enableDarkTheme: boolean;
  selectedAsset: Asset;
  latestTagData: any;
  hierarchyInfo: any;
  setIsSelectModalOpen: any;
  setIsFitmentViewOpen: any;
  setIsTirePositionOpen: any;
  setIsAxleWiseModalOpen: any;
  metricType: string;
  tireData: any;
  batteryData: any;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  let assetTireData = {
    pressureData: {},
    notifications: [],
    latestTireTimeStamp: 0,
    tireFitmentData: [],
    fitmentHistory: [],
  };

  if (selectedAsset) {
    assetTireData = tireData[selectedAsset?.device.deviceId] ?? {
      pressureData: {},
      notifications: [],
      latestTireTimeStamp: 0,
      tireFitmentData: [],
      fitmentHistory: [],
    };
  }
  const assetHasTpmSSensor = isSensorEnabled(assetTireData);

  // Get battery data for this asset
  const deviceBattery = selectedAsset?.device?.deviceId
    ? batteryData?.[selectedAsset.device.deviceId]
    : undefined;

  // Map tire codes to position names
  const getTirePositionName = (tireCode: string) => {
    const positionMap: Record<string, string> = {
      '0x17': 'Front Left',
      '0x19': 'Front Right',
      '0x26': 'Rear Outside Left',
      '0x27': 'Rear Inside Left',
      '0x29': 'Rear Inside Right',
      '0x2A': 'Rear Outside Right',
    };
    return positionMap[tireCode] || tireCode;
  };

  // Get low battery tires
  const lowBatteryTires = deviceBattery
    ? Object.entries(deviceBattery)
        .filter(([_, data]: [string, any]) => data?.isBatteryLow)
        .map(([tireCode, _]) => getTirePositionName(tireCode))
    : [];

  const canEdit = useRoleBasedAccess([
    'director',
    'internal-user',
    'external-admin',
    'external-superadmin',
    'external-director',
  ]);

  return (
    <Grid container spacing={2} alignItems="center">
      <AssetHeader
        enableDarkTheme={enableDarkTheme}
        selectedAsset={selectedAsset}
        latestTagData={latestTagData}
        hierarchyInfo={hierarchyInfo}
      />
      {assetHasTpmSSensor && canEdit && (
        <Grid item xs={12} lg={5}>
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            flexWrap={'wrap'}
            gap={1}
          >
            <Button
              sx={{
                backgroundColor: ThemePalette.typography.primary,
                textTransform: 'none',
                color: ThemePalette.typography.white,
                fontSize: '12px',
                padding: '10px',
                paddingLeft: '20px',
                paddingRight: '20px',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color: enableDarkTheme ? 'white' : 'black',
                  border: enableDarkTheme
                    ? '1px solid white'
                    : '1px solid black',
                },
              }}
              onClick={() => {
                setIsFitmentViewOpen(true);
              }}
              startIcon={
                <CalendarToday
                  fontSize="small"
                  sx={{
                    fontSize: '12px',
                  }}
                />
              }
            >
              {t('set_fitment_details')}
            </Button>
            {/* <Button
              sx={{
                backgroundColor: ThemePalette.typography.primary,
                textTransform: 'none',
                color: ThemePalette.typography.white,
                fontSize: '12px',
                padding: '10px',
                paddingLeft: '20px',
                paddingRight: '20px',
                '&:hover': {
                  color: enableDarkTheme ? 'white' : 'black',
                  border: enableDarkTheme
                    ? '1px solid white'
                    : '1px solid black',
                },
              }}
              onClick={() => {
                setIsSelectModalOpen(true);
              }}
              startIcon={
                <Edit
                  fontSize="small"
                  sx={{
                    fontSize: '12px',
                  }}
                />
              }
            >
              Set Thresholds
            </Button> */}

            <Button
              sx={{
                backgroundColor: ThemePalette.typography.primary,
                textTransform: 'none',
                color: ThemePalette.typography.white,
                fontSize: '12px',
                padding: '10px',
                paddingLeft: '20px',
                paddingRight: '20px',
                '&:hover': {
                  color: enableDarkTheme ? 'white' : 'black',
                  border: enableDarkTheme
                    ? '1px solid white'
                    : '1px solid black',
                },
              }}
              onClick={() => {
                setIsAxleWiseModalOpen(true);
              }}
              startIcon={
                <Edit
                  fontSize="small"
                  sx={{
                    fontSize: '12px',
                  }}
                />
              }
            >
              {t('set_thresholds')}
            </Button>

            <Button
              sx={{
                backgroundColor: ThemePalette.typography.primary,
                textTransform: 'none',
                color: ThemePalette.typography.white,
                fontSize: '12px',
                padding: '10px',
                paddingLeft: '20px',
                paddingRight: '20px',
                '&:hover': {
                  color: enableDarkTheme ? 'white' : 'black',
                  border: enableDarkTheme
                    ? '1px solid white'
                    : '1px solid black',
                },
              }}
              onClick={() => {
                setIsTirePositionOpen(true);
              }}
              startIcon={
                <DynamicFeed
                  fontSize="small"
                  sx={{
                    fontSize: '12px',
                  }}
                />
              }
            >
              {t('swap_tires')}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
